/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import AppInfo from './components/auth/AppInfo';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/common/Layout';
import Dashboard from './components/common/Dashboard';
import PdfList from './components/pdfs/PdfList';
import PdfDetail from './components/pdfs/PdfDetail';
import CreatePdf from './components/pdfs/CreatePdf';
import EditPdf from './components/pdfs/EditPdf';
import BidList from './components/bids/BidList';
import CreateBid from './components/bids/CreateBid';
import EditBid from './components/bids/EditBid';
import ViewBid from './components/bids/ViewBid';
import BidResultForm from './components/bids/BidResultForm';
import ParticipantList from './components/participants/ParticipantList';
import CreateParticipant from './components/participants/CreateParticipant';
import UserProfileForm from './components/participants/UserProfileForm';
import ManagerParticipantForm from './components/participants/ManagerParticipantForm';
import AdminParticipantForm from './components/participants/AdminParticipantForm';
import ChangePassword from './components/participants/ChangePassword';
import ActivityReport from './components/reports/ActivityReport';
import ManagerReport from './components/reports/ManagerReport';
import MonthlyDetailPage from './components/reports/MonthlyDetailPage';
import ExecutiveReport from './components/reports/ExecutiveReport';
import MonthlyBidSummary from './components/reports/MonthlyBidSummary';
import ExecutiveViewBid from './components/bids/ExecutiveViewBid';
import ExecutivePurchaseCredits from './components/executive/ExecutivePurchaseCredits';
import ManagerCompanyForm from './components/companies/ManagerCompanyForm';
import CompanyList from './components/companies/CompanyList';
import AdminCompanyForm from './components/companies/AdminCompanyForm';
import CompanyCalendar from './components/calendar/CompanyCalendar';
import { ParticipantType } from './types';
import './App.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/app-info" element={<AppInfo />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bids/create"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.USER]}>
                <Layout>
                  <CreateBid />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bids/:id/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <EditBid />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bids/:id/view"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN, ParticipantType.MANAGER]}>
                <Layout>
                  <ViewBid />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bids/:bidId/results"
            element={
              <ProtectedRoute>
                <Layout>
                  <BidResultForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bids"
            element={
              <ProtectedRoute>
                <Layout>
                  <BidList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/components/create"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER, ParticipantType.ADMIN]}>
                <Layout>
                  <CreatePdf />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/components/:id/edit"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER, ParticipantType.ADMIN]}>
                <Layout>
                  <EditPdf />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/components/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <PdfDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/components"
            element={
              <ProtectedRoute>
                <Layout>
                  <PdfList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/participants/create"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <AdminParticipantForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/participants/:id/edit"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <AdminParticipantForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/participants/user/create"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER]}>
                <Layout>
                  <ManagerParticipantForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/participants/user/:id/edit"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER]}>
                <Layout>
                  <ManagerParticipantForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserProfileForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChangePassword />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/participants"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN, ParticipantType.MANAGER]}>
                <Layout>
                  <ParticipantList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER]}>
                <Layout>
                  <ManagerCompanyForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <Layout>
                  <CompanyCalendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <CompanyList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/create"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <AdminCompanyForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/:id/edit"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <AdminCompanyForm />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/activity"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <ActivityReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/team"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER, ParticipantType.ADMIN]}>
                <Layout>
                  <ManagerReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/activity/:yearMonth"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.ADMIN]}>
                <Layout>
                  <MonthlyDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/team/:yearMonth"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.MANAGER, ParticipantType.ADMIN]}>
                <Layout>
                  <MonthlyDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/executive/report"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.EXECUTIVE]}>
                <Layout>
                  <ExecutiveReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/executive/summary/:year/:month/:category"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.EXECUTIVE]}>
                <Layout>
                  <MonthlyBidSummary />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/executive/bid/:id"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.EXECUTIVE]}>
                <Layout>
                  <ExecutiveViewBid />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/executive/purchase-credits"
            element={
              <ProtectedRoute allowedRoles={[ParticipantType.EXECUTIVE]}>
                <Layout>
                  <ExecutivePurchaseCredits />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
